import { type DragEndEvent } from "@dnd-kit/core"
import { arrayMove } from "@dnd-kit/sortable"
import { IconBrandLoom, IconBrowser, IconEye, IconHeadphones, IconSparkles } from "@tabler/icons-react"
import { type FC, Fragment, memo, useCallback, useEffect, useLayoutEffect, useState } from "react"

import { type IntendedPageType } from "@forento/core/models/course"
import routes from "@forento/core/routes"

import Button from "~/components/Button"
import {
	CustomIcon,
	DeleteIcon,
	EditIcon,
	InputIcon,
	QuizIcon,
	TextIcon,
	UploadIcon,
	VideoIcon,
	VimeoIcon,
	YouTubeIcon,
} from "~/components/Icon"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import { useAlert } from "~/contexts/AlertContext"
import SortableListContext from "~/contexts/SortableListContext"
import { useUser } from "~/contexts/UserContext"
import useDropdownMenu from "~/hooks/useDropdownMenu"
import trpc, { query } from "~/utilities/trpc"

import EditPageModal from "./EditPageModal"
import { FreePreviewEnd } from "./FreePreview"
import Page from "./Page"

type Props = {
	chapterId: number
	editPageId: number | null
	setEditPageId(pageId: number | null): void
	reloadCourse(): Promise<unknown>
	// The preview is the course's first `pageCount` pages. Null for a free course, which has nothing to preview.
	freePreview: { pagesBeforeChapter: number; pageCount: number } | null
	onGenerateWithAi(): void
}
const ChapterEditor: FC<Props> = ({
	chapterId,
	editPageId,
	setEditPageId,
	reloadCourse,
	freePreview,
	onGenerateWithAi,
}) => {
	const user = useUser()
	const alert = useAlert()
	const platform = user.user!.platform!

	const {
		data: chapter,
		error,
		refetch,
	} = query.course.getChapter.useQuery(chapterId, { refetchOnWindowFocus: false })

	const [pagesOrder, setPagesOrder] = useState<number[]>([])
	const [cachedPage, setCachedPage] = useState<NonNullable<typeof chapter>["pages"][number]>()

	useEffect(() => {
		if (chapter) {
			const editPage = chapter.pages.find(x => x.id === editPageId)
			if (editPage) {
				setCachedPage(editPage)
			}
		}
	}, [chapter, editPageId])

	const dropdownMenu = useDropdownMenu([
		{ label: "Upload", icon: UploadIcon, onClick: () => handleCreateNewPage("video") },
		{ label: "YouTube", icon: YouTubeIcon, onClick: () => handleCreateNewPage("youtube") },
		{ label: "Vimeo", icon: VimeoIcon, onClick: () => handleCreateNewPage("vimeo") },
		{ label: "Loom", icon: IconBrandLoom, onClick: () => handleCreateNewPage("loom") },
	])

	useLayoutEffect(() => {
		if (chapter == null) {
			setPagesOrder([])
			return
		}
		setPagesOrder(chapter.pages.map(page => page.id))
	}, [chapter])

	const reload = useCallback(async () => {
		await Promise.all([reloadCourse(), refetch()])
	}, [reloadCourse, refetch])

	const handleClosePage = useCallback(
		async (args?: { reloadCourse?: boolean }) => {
			setEditPageId(null)
			if (args?.reloadCourse) {
				await reload()
			}
		},
		[reload, setEditPageId],
	)

	if (error) return <p>Failed to load chapter.</p>
	if (chapter === null) return <p>Chapter not found.</p>
	if (chapter === undefined) return <PartialLoadingPage />

	const handleEditChapterTitle = async () => {
		const dialog = await alert.input(
			"Edit chapter title",
			"Enter the new title for this chapter",
			"Chapter title",
			chapter.title,
		)
		if (dialog.result === null) return

		try {
			await trpc.course.updateChapter.mutate({ id: chapter.id, data: { title: dialog.result } })
			await reload()
		} catch (error) {
			console.error(error)
			dialog.close()
		}
		dialog.close()
	}

	const handleDeleteChapter = async () => {
		const dialog = await alert.confirm(
			"Delete chapter",
			"Are you sure you want to delete this chapter? All pages will be deleted as well.",
		)
		if (!dialog.result) return

		try {
			await trpc.course.deleteChapter.mutate(chapter.id)
			await reload()
		} catch (error) {
			console.error(error)
			dialog.close()
		}
		dialog.close()
	}

	const handleCreateNewPage = async (type: IntendedPageType) => {
		const page = await trpc.course.createPage.mutate({ chapterId: chapter.id, intendedType: type })
		await reload()
		setEditPageId(page.id)
	}

	const handlePageDragEnd = async (event: DragEndEvent) => {
		const { active, over } = event
		if (over === null || over.id === active.id) return

		const newOrder = arrayMove(
			pagesOrder,
			pagesOrder.indexOf(Number(active.id)),
			pagesOrder.indexOf(Number(over.id)),
		)
		setPagesOrder(newOrder)

		for (const page of chapter.pages) {
			await trpc.course.updatePage.mutate({ id: page.id, data: { sortOrder: newOrder.indexOf(page.id) } })
		}
	}

	const sortedPages = [...chapter.pages].sort((a, b) => pagesOrder.indexOf(a.id) - pagesOrder.indexOf(b.id))
	// How many of this chapter's pages the free preview covers. It may continue into later chapters.
	const previewedPageCount =
		freePreview !== null ? Math.max(freePreview.pageCount - freePreview.pagesBeforeChapter, 0) : 0

	const createPageButtons = [
		{ label: "Video", icon: VideoIcon, ...dropdownMenu.button },
		{ label: "Audio", icon: IconHeadphones, onClick: () => handleCreateNewPage("audio") },
		{ label: "Embed", icon: IconBrowser, onClick: () => handleCreateNewPage("iframe") },
		{ label: "Text", icon: TextIcon, onClick: () => handleCreateNewPage("text") },
		{ label: "Input", icon: InputIcon, onClick: () => handleCreateNewPage("input") },
		{ label: "Quiz", icon: QuizIcon, onClick: () => handleCreateNewPage("quiz") },
		{ label: "Custom", icon: CustomIcon, onClick: () => handleCreateNewPage("custom") },
	]

	return (
		<div className="mb-8 flex w-full max-w-4xl flex-col p-8 pb-12">
			{dropdownMenu.component}
			{cachedPage && (
				<EditPageModal
					isOpen={editPageId !== null}
					type={cachedPage.intendedType}
					iframeAllowedDomains={chapter.iframeAllowedDomains}
					onClose={handleClosePage}
					page={cachedPage}
				/>
			)}
			<div className="mb-8 flex flex-wrap items-center justify-between gap-4">
				<div className="flex items-center gap-2 text-2xl font-semibold">
					{chapter.title}
					<button
						type="button"
						onClick={handleEditChapterTitle}
						aria-label="Rename chapter"
						className="focus-visible:outline-primary cursor-pointer rounded-md focus-visible:outline-2 focus-visible:outline-offset-2"
					>
						<EditIcon className="size-6" />
					</button>
				</div>
				<div className="flex gap-4">
					<button
						type="button"
						onClick={handleDeleteChapter}
						aria-label="Delete chapter"
						className="bg-danger hover:bg-danger/80 focus-visible:outline-primary cursor-pointer rounded-lg p-3 text-white transition-colors focus-visible:outline-2 focus-visible:outline-offset-2"
					>
						<DeleteIcon className="size-5" />
					</button>
					<Button
						variant="primary"
						onClick={`//${platform.domain.platform}${routes.platform.course.player(
							chapter.courseId,
						)}?email=${encodeURIComponent(user.user!.email)}`}
						newTab
					>
						Preview
					</Button>
				</div>
			</div>
			<div className="bg-component-background mb-6 flex flex-wrap justify-between gap-4 rounded-lg px-6 py-4">
				<div className="font-semibold whitespace-nowrap">Create a new page</div>
				<div className="flex gap-3">
					{createPageButtons.map(({ icon: Icon, label, ...button }) => (
						<button
							key={label}
							type="button"
							className="flex cursor-pointer items-center gap-1.5 text-sm font-medium"
							{...button}
						>
							<Icon className="size-6" />
							{label}
						</button>
					))}
				</div>
			</div>
			{freePreview !== null && previewedPageCount > sortedPages.length && sortedPages.length > 0 && (
				<p className="text-primary mb-4 flex items-center gap-1.5 text-sm font-medium">
					<IconEye className="size-4" />
					Every page in this chapter is part of the free preview.
				</p>
			)}
			<div className="flex flex-col">
				<SortableListContext items={pagesOrder} onDragEnd={handlePageDragEnd}>
					{sortedPages.map((page, index) => (
						<Fragment key={page.id}>
							<Page
								page={page}
								order={pagesOrder.indexOf(page.id)}
								maxPageOrder={chapter.pages.length}
								onClick={() => setEditPageId(page.id)}
							/>
							{previewedPageCount === index + 1 ? (
								<FreePreviewEnd courseId={chapter.courseId} />
							) : (
								index < sortedPages.length - 1 && <div className="h-4" />
							)}
						</Fragment>
					))}
				</SortableListContext>
			</div>
			{chapter.pages.length >= 5 && platform?.enabledFeatures.includes("ai-generated-courses") && (
				<button
					type="button"
					onClick={onGenerateWithAi}
					className="border-primary/30 bg-primary/5 hover:bg-primary/10 mt-6 flex cursor-pointer items-center gap-3 self-start rounded-lg border px-4 py-3 text-left transition-colors"
				>
					<IconSparkles className="text-primary size-5 shrink-0" />
					<span className="text-sm">
						<span className="text-primary font-medium">Generate the next chapter with AI</span>
						<span className="ml-1 text-gray-500">— let Forento AI continue building your course</span>
					</span>
				</button>
			)}
		</div>
	)
}

export default memo(ChapterEditor)
